import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, X, Loader2, Search, Image as ImageIcon, Send } from "lucide-react";

/**
 * Floating "Quick Capture" button for admins.
 *
 * Tap → opens rear camera (`capture="environment"`) on mobile/tablet, or a file
 * picker on desktop. After a photo is taken, a modal lists the 20 most recent
 * ACTIVE jobs (anything except Completed). Admin picks one and the photo lands
 * on that job's timeline via the existing /admin/jobs/{id}/timeline endpoint.
 *
 * Caption is optional. Single photo per tap (multi-photo would mean staging UI
 * — keeping it deliberate so admin doesn't accidentally batch the wrong job).
 */
const ACTIVE_STATUSES = ["New", "Awaiting Drop-off", "In Progress", "Waiting on Parts", "Ready for Pickup"];

export const QuickCapture = () => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [media, setMedia] = useState(null); // resolved /upload response
  const [caption, setCaption] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [query, setQuery] = useState("");
  const [attaching, setAttaching] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setMedia(null);
    setCaption("");
    setQuery("");
    setAttaching(false);
    setUploadingMedia(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Lazy-load active jobs as soon as we have a photo (so the picker is ready fast).
  useEffect(() => {
    if (!file) return;
    let dead = false;
    setLoadingJobs(true);
    api.get("/admin/jobs")
      .then((res) => {
        if (dead) return;
        const active = (res.data || [])
          .filter((j) => ACTIVE_STATUSES.includes(j.status))
          .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""))
          .slice(0, 30);
        setJobs(active);
      })
      .catch(() => !dead && setJobs([]))
      .finally(() => !dead && setLoadingJobs(false));
    return () => { dead = true; };
  }, [file]);

  // Upload the photo to object storage right after capture (parallel with admin picking the job).
  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setUploadingMedia(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
      reset();
    } finally {
      setUploadingMedia(false);
    }
  };

  const filteredJobs = jobs.filter((j) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [j.ref_number, j.full_name, j.scooter_brand, j.scooter_model].some((v) => (v || "").toLowerCase().includes(q));
  });

  const attachTo = async (job) => {
    if (!media) {
      toast.error("Still uploading — give it a moment");
      return;
    }
    setAttaching(true);
    try {
      await api.post(`/admin/jobs/${job.id}/timeline`, {
        message: caption.trim() || `Workshop photo (${new Date().toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })})`,
        media: [media],
      });
      toast.success(`Photo attached to ${job.ref_number}`);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't attach photo");
    } finally {
      setAttaching(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => fileRef.current?.click()}
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 size-14 rounded-full bg-spark-yellow text-spark-ink shadow-xl flex items-center justify-center hover:bg-yellow-400 hover:scale-105 transition group"
        aria-label="Quick capture — take a photo for a job"
        data-testid="quick-capture-btn"
        title="Quick capture"
      >
        <Camera className="size-6" />
        <span className="absolute right-full mr-3 px-3 py-1.5 rounded-md bg-spark-ink text-white text-xs uppercase tracking-wider font-bold whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition hidden sm:block">
          Quick capture
        </span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFilePicked}
        data-testid="quick-capture-input"
      />

      {/* Job picker modal */}
      {file && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="quick-capture-modal">
          <div className="w-full max-w-lg rounded-xl border border-spark-teal/30 bg-spark-ink shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="bg-spark-teal/10 border-b border-spark-teal/30 px-5 py-4 flex items-center gap-3">
              <Camera className="size-5 text-spark-teal" />
              <h2 className="font-display uppercase tracking-wider text-base font-bold text-spark-teal flex-1">Attach photo to a job</h2>
              <button onClick={reset} disabled={attaching} className="text-slate-400 hover:text-white p-1 rounded-sm hover:bg-white/5" aria-label="Close" data-testid="quick-capture-close">
                <X className="size-4" />
              </button>
            </div>

            <div className="p-4 sm:p-5 grid sm:grid-cols-[120px_1fr] gap-3 sm:gap-4 border-b border-white/5">
              <div className="relative size-24 sm:size-30 rounded-md overflow-hidden bg-black/40 border border-white/10 shrink-0">
                {preview && <img src={preview} alt="Captured" className="w-full h-full object-cover" />}
                {uploadingMedia && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Loader2 className="size-5 animate-spin text-spark-yellow" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Caption (optional)</label>
                <textarea
                  rows={2}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="e.g. Brake pads worn — replacing today"
                  className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal resize-none"
                  data-testid="quick-capture-caption"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  {uploadingMedia ? "Uploading photo…" : media ? "Photo ready · pick a job below" : "Photo upload failed — try again"}
                </p>
              </div>
            </div>

            <div className="px-4 sm:px-5 pt-3 pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-500" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by ref, customer or scooter…"
                  className="w-full pl-9 pr-3 py-2 bg-spark-teal-night border border-white/10 rounded-md text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-spark-teal"
                  data-testid="quick-capture-search"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-5 pb-5" data-testid="quick-capture-job-list">
              {loadingJobs ? (
                <div className="p-8 text-center"><Loader2 className="size-5 text-spark-yellow animate-spin inline" /></div>
              ) : filteredJobs.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-8">
                  {jobs.length === 0 ? "No active jobs right now." : "No jobs match your search."}
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {filteredJobs.map((j) => (
                    <li key={j.id}>
                      <button
                        type="button"
                        onClick={() => attachTo(j)}
                        disabled={attaching || uploadingMedia || !media}
                        className="w-full text-left px-3 py-2.5 rounded-md bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 hover:border-spark-teal/40 transition flex items-center gap-3 group disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`quick-capture-pick-${j.ref_number}`}
                      >
                        <div className="size-9 rounded-md bg-spark-teal/15 text-spark-teal flex items-center justify-center shrink-0 group-hover:bg-spark-teal group-hover:text-white transition">
                          <ImageIcon className="size-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-bold text-spark-yellow text-xs">{j.ref_number}</span>
                            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300">{j.status}</span>
                          </div>
                          <p className="text-sm text-white truncate">{j.full_name}</p>
                          <p className="text-[11px] text-slate-400 truncate">{j.scooter_brand} {j.scooter_model}</p>
                        </div>
                        <Send className="size-4 text-slate-500 group-hover:text-spark-yellow transition shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {attaching && (
              <div className="px-5 py-3 bg-spark-yellow/10 border-t border-spark-yellow/30 text-spark-yellow text-xs uppercase tracking-wider font-bold flex items-center gap-2">
                <Loader2 className="size-3.5 animate-spin" /> Attaching to job…
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default QuickCapture;
